import { useCallback, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { FACILITY_LOCATIONS_KEY, FACILITY_OVERVIEW_KEY } from '@/hooks/useFacility'
import { facilityApi } from '@/api/facilityApi'
import { equipmentApi, type ApiEquipmentType, type ApiEquipmentUnit, type ReturnToServicePayload } from '@/api/equipmentApi'
import { mockEquipment } from '@/mocks/equipment'
import { useMockApi } from '@/lib/useMock'
import { useTenant } from '@/providers/TenantProvider'
import { useGroups, type EquipmentGroupRow, type GroupMutations } from '@/hooks/useGroups'
import {
  DEFAULT_EQUIPMENT_PURPOSE,
  type CalendarEventEntry,
  type Cycle,
  type CycleProcedures,
  type Equipment,
  type EquipmentGroup,
  type EquipmentPurpose,
  type EquipmentSchedule,
  type EquipmentTypeRow,
  type EventType,
} from '@/types/equipment'
import { deriveEquipmentTypes } from '@/lib/equipment'
import { userDisplayName } from '@/lib/userDisplayName'

// ─── Raw equipment-type query (unadapted registry types) ────────────────────

/**
 * Raw equipment-type registry rows for the selected tenant. Unlike `useEquipment`
 * (which adapts units into the planner's domain shapes) this exposes the backend
 * `ApiEquipmentType[]` directly — used by the process-step equipment editor, which
 * needs the real `id`/`group`/`unitCount` to build requirement rows.
 */
export function useEquipmentTypes(enabled = true) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: ['equipment', 'types', tenantId],
    enabled: !!tenantId && enabled,
    queryFn: () => equipmentApi.listTypes(tenantId as string),
  })
}

/**
 * Raw equipment units for the selected tenant. A unit's `label` is its
 * "Equipment ID" (unique per tenant, e.g. "Biosafety cabinet-A1") — used by the
 * process-step equipment editor to pick a specific unit.
 */
export function useEquipmentUnits(enabled = true) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: ['equipment', 'units', tenantId],
    enabled: !!tenantId && enabled,
    queryFn: () => equipmentApi.listEquipment(tenantId as string),
  })
}

// ─── Backend → frontend adapter ─────────────────────────────────────────────

function errorText(err: unknown): string {
  return err instanceof Error ? err.message : 'unknown error'
}

function locationDisplay(unit: ApiEquipmentUnit): string {
  const parts = [
    unit.locationSite,
    unit.locationFloor && `Floor-${unit.locationFloor}`,
    unit.locationBay,
  ]
  // Seeded suites store the whole display in locationSite (e.g. "Suite-A").
  return parts.filter(Boolean).join(' / ') || '—'
}

function toSchedule(type: ApiEquipmentType): EquipmentSchedule {
  const schedule: EquipmentSchedule = {}
  for (const s of type.schedules) {
    if (s.eventTypes.length === 0) continue
    schedule[s.cycle] = {
      eventTypes: s.eventTypes,
      note: s.note ?? undefined,
      weeklyDays: s.weeklyDays ?? undefined,
      preferredTiming: s.preferredTiming ?? undefined,
      startTime: s.startTime ?? undefined,
      endTime: s.endTime ?? undefined,
      procedures: s.procedures?.length
        ? Object.fromEntries(s.procedures.map((p) => [p.eventTypeCode, { id: p.procedureTemplate.id, name: p.procedureTemplate.name }]))
        : undefined,
    }
  }
  return schedule
}

function toCalendar(type: ApiEquipmentType): { events: CalendarEventEntry[] } | undefined {
  if (type.calendarEvents.length === 0) return undefined
  return {
    events: type.calendarEvents.map((ev) => ({
      id: ev.id,
      date: ev.date.slice(0, 10),
      event: ev.event,
      note: ev.note ?? undefined,
    })),
  }
}

/**
 * Registry type → planner row.
 *
 * The planner reads types from here rather than collapsing units, because the
 * two disagree exactly where it matters: a type exists the moment it is created,
 * with no units yet, and creating the first unit of it needs the type to already
 * be pickable.
 */
function adaptType(type: ApiEquipmentType): EquipmentTypeRow {
  return {
    id: type.id,
    equipmentType: type.name,
    group: type.group.name,
    // Same fallback as `adapt`: the column is new, and a backend predating it
    // would otherwise leave Purpose blank.
    purpose: type.purpose ?? DEFAULT_EQUIPMENT_PURPOSE,
    // Same fallback again: absent means maintained, which is the column
    // default, so a backend predating the column keeps today's behaviour.
    maintained: type.maintained ?? true,
    cleanHoldTimeHours: type.cleanHoldTimeHours ?? null,
    // Counted server-side — the units list here may be filtered.
    units: type.unitCount,
    schedule: toSchedule(type),
    calendar: toCalendar(type),
    preStartChecklist: type.preStartChecklist,
    defaultAssigneeId: type.defaultAssigneeId ?? undefined,
    defaultAssigneeName: type.defaultAssignee
      ? userDisplayName(type.defaultAssignee)
      : undefined,
  }
}

function adapt(units: ApiEquipmentUnit[], types: ApiEquipmentType[]): Equipment[] {
  const typeById = new Map(types.map((t) => [t.id, t]))
  return units.map((unit) => {
    const type = typeById.get(unit.equipmentTypeId)
    return {
      id: unit.id,
      equipmentId: unit.label,
      equipmentType: unit.equipmentType.name,
      equipmentTypeId: unit.equipmentTypeId,
      group: unit.equipmentType.group.name,
      // Falls back rather than trusting the field to be present: the column is
      // new, and a response from a backend that predates it would otherwise
      // leave the grid showing an empty Purpose cell.
      purpose: unit.equipmentType.purpose ?? DEFAULT_EQUIPMENT_PURPOSE,
      // Inherited from the type for the same reason purpose is: a unit is not
      // serviced on its own cycle, its type is. Absent means maintained, the
      // column default.
      maintained: unit.equipmentType.maintained ?? true,
      // The site layout says where it is; the old free text only until it is mapped.
      location: { site: unit.locationSite ?? '—', display: unit.place || locationDisplay(unit) },
      description: unit.description ?? '',
      model: unit.model ?? '—',
      serial: unit.serial ?? '—',
      vendor: unit.vendor ?? '—',
      installed: unit.installedYear ?? 0,
      status: unit.status,
      // Only an explicit null is "not on a floor": a backend that does not
      // send the field yet must not mark every room as unmapped.
      floorId: unit.floorId,
      roomId: unit.roomId,
      grade: unit.grade ?? null,
      areaSqm: unit.areaSqm ?? null,
      schedule: type ? toSchedule(type) : {},
      calendar: type ? toCalendar(type) : undefined,
      preStartChecklist: type?.preStartChecklist,
    }
  })
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export type NewUnitInput = {
  label: string
  equipmentType: string
  group: EquipmentGroup
  model?: string
  serial?: string
  vendor?: string
  installed?: number
  /** The room it stands in (z8nrz7fw25); omitted or null: in none yet. */
  roomId?: string | null
  /** Rooms only: the floor to put it on once created; omitted or null: on none yet. */
  floorId?: string | null
}

export type EquipmentMutations = {
  /** Upsert one cycle cell for an equipment type (empty eventTypes deletes it). */
  saveSchedule: (
    equipmentType: string,
    cycle: Cycle,
    eventTypes: EventType[],
    note?: string,
    weeklyDays?: { day: number; state: 'required' | 'preferred' }[],
    preferredTiming?: string,
    startTime?: string,
    endTime?: string,
    /** The procedure each event type runs (z8nrz7f9u6): the whole set; undefined leaves them as they are. */
    procedures?: CycleProcedures,
  ) => Promise<void>
  /** Replace the non-routine calendar events for an equipment type (diff-based). */
  saveCalendar: (equipmentType: string, events: CalendarEventEntry[]) => Promise<void>
  /** Register a new unit; creates the equipment type first when it doesn't exist. */
  /**
   * Resolves to the new unit's id, or null in mock mode where no backend row
   * exists. The caller needs it to offer the asset tag straight after creation —
   * the tag's symbol encodes this id, and it is the one thing the create form
   * cannot know in advance.
   */
  addEquipment: (input: NewUnitInput) => Promise<string | null>
  /** Create a new equipment type. Units are added separately, on purpose. */
  addEquipmentType: (input: {
    name: string
    group: EquipmentGroup
    /** Omitted means PROCESSING — the column default decides, not the caller. */
    purpose?: EquipmentPurpose
    /** Omitted means maintained — the column default decides, not the caller. */
    maintained?: boolean
    preStartChecklist?: { label: string; required?: boolean }[]
    /** Omitted means no default — generated PM tasks stay unassigned. */
    defaultAssigneeId?: string
    /** Room types only (Cleaning Visual). */
    cleanHoldTimeHours?: number
  }) => Promise<void>
  /** Edit an existing equipment type's name, group, purpose, and/or pre-start checklist (full-replace). */
  updateEquipmentType: (input: {
    id: string
    name?: string
    group?: EquipmentGroup
    /** What the machine is FOR. Editable here because the edit modal offers it. */
    purpose?: EquipmentPurpose
    /** Turning this off stops all scheduling for the type. */
    maintained?: boolean
    preStartChecklist?: { label: string; required?: boolean }[]
    /** Omitted leaves it unchanged; `''` clears an existing default. */
    defaultAssigneeId?: string
    /** Room types only: omitted leaves it, null clears it. */
    cleanHoldTimeHours?: number | null
  }) => Promise<void>
  /** Edit a unit's own fields (label, model, serial, vendor, installed, location). */
  editEquipment: (
    id: string,
    input: {
      label: string
      model?: string
      serial?: string
      vendor?: string
      installed?: number
      /** Rooms only (Cleaning Visual). Undefined leaves it; null clears it. */
      grade?: string | null
      areaSqm?: number | null
      /** The room it stands in (z8nrz7fw25). Undefined leaves it; null takes it out. */
      roomId?: string | null
      /** Rooms only: the floor it is on. Undefined leaves it; null takes it off. */
      floorId?: string | null
    },
  ) => Promise<void>
} & GroupMutations

/**
 * Equipment fleet for the selected tenant, adapted to the planner's domain
 * shapes, plus mutations. Falls back to the in-memory mock when
 * `VITE_USE_MOCK=true` (mutations then only update the query cache).
 */
export function useEquipment(
  /**
   * Only equipment of these purposes — the Equipment page passes Processing and
   * Storage, the Cleaning page Room (z8nrz7ewt2). Omitted: every purpose.
   */
  purposes?: readonly EquipmentPurpose[],
): {
  data: Equipment[] | undefined
  isLoading: boolean
  error: string | null
  groups: EquipmentGroupRow[]
  /** Every equipment type in the tenant, including ones with no units yet. */
  types: EquipmentTypeRow[]
  mutations: EquipmentMutations
} {
  const queryClient = useQueryClient()
  const { selectedTenantId } = useTenant()
  const tenantId = useMockApi ? 'mock' : selectedTenantId
  const [mockTypes, setMockTypes] = useState<EquipmentTypeRow[]>([])
  const {
    groups,
    resolveGroupId,
    addGroup: addGroupBase,
    renameGroup: renameGroupBase,
    removeGroup: removeGroupBase,
  } = useGroups(purposes)

  const scope = purposes?.length ? purposes.join(',') : 'all'
  const queryKey = useMemo(() => ['equipment', tenantId, scope], [tenantId, scope])

  const query = useQuery({
    queryKey,
    enabled: tenantId !== null,
    queryFn: async (): Promise<Equipment[]> => {
      if (useMockApi) return mockEquipment.filter((e) => !purposes?.length || purposes.includes(e.purpose))
      const [units, types] = await Promise.all([
        equipmentApi.listEquipment(tenantId as string, purposes),
        equipmentApi.listTypes(tenantId as string, purposes),
      ])
      return adapt(units, types)
    },
  })

  // The registry, shared with `useEquipmentTypes` by key so both read one cache
  // entry. Separate from the query above because a type with no units has to
  // survive: the units list cannot represent it.
  // A filtered registry gets its own entry, under the shared prefix: the
  // unfiltered one belongs to `useEquipmentTypes`, whose pickers list every type.
  const typesQuery = useQuery({
    queryKey: scope === 'all' ? ['equipment', 'types', tenantId] : ['equipment', 'types', tenantId, scope],
    enabled: !useMockApi && tenantId !== null,
    queryFn: () => equipmentApi.listTypes(tenantId as string, purposes),
  })

  const types = useMemo(
    () =>
      useMockApi
        ? [...deriveEquipmentTypes(mockEquipment), ...mockTypes].filter(
            (t) => !purposes?.length || purposes.includes(t.purpose),
          )
        : (typesQuery.data ?? []).map(adaptType),
    [typesQuery.data, mockTypes, purposes],
  )

  // Prefix invalidation, so the registry (['equipment','types',…]) and the raw
  // units list refresh alongside the adapted fleet. Keying on `queryKey` alone
  // would leave a stale unit count on every type row after adding a unit.
  // The Overall View too: a type's clean hold time, a room's grade or status
  // all change what it shows. And Tasks: saving a cycle, a calendar event or a
  // unit creates (and a changed cycle removes) its upcoming tasks (z8nrz7f9u6).
  // The site layout and the room pickers as well: a room added, renamed or
  // removed here changes what Building set-up can map and what the pickers
  // offer. Without them a new room only appeared after a browser refresh
  // (NYP feedback, 2026-10-29).
  const refetch = useCallback(
    () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ['equipment'] }),
        queryClient.invalidateQueries({ queryKey: [FACILITY_OVERVIEW_KEY] }),
        queryClient.invalidateQueries({ queryKey: ['maintenance-events'] }),
        queryClient.invalidateQueries({ queryKey: ['facility-layout'] }),
        queryClient.invalidateQueries({ queryKey: [FACILITY_LOCATIONS_KEY] }),
      ]),
    [queryClient],
  )

  /** Optimistically patch every cached unit of a type. */
  const patchType = useCallback(
    (equipmentType: string, patch: (eq: Equipment) => Equipment) => {
      queryClient.setQueryData<Equipment[]>(queryKey, (prev) =>
        prev?.map((eq) => (eq.equipmentType === equipmentType ? patch(eq) : eq)),
      )
    },
    [queryClient, queryKey],
  )

  // Units first (they carry the id inline), then the registry — a type with no
  // units yet is still configurable, and only the registry knows about it.
  const typeIdFor = useCallback(
    (equipmentType: string): string | undefined =>
      queryClient
        .getQueryData<Equipment[]>(queryKey)
        ?.find((eq) => eq.equipmentType === equipmentType)?.equipmentTypeId ??
      types.find((t) => t.equipmentType === equipmentType)?.id,
    [queryClient, queryKey, types],
  )

  const saveSchedule = useCallback<EquipmentMutations['saveSchedule']>(
    async (equipmentType, cycle, eventTypes, note, weeklyDays, preferredTiming, startTime, endTime, procedures) => {
      patchType(equipmentType, (eq) => {
        const schedule = { ...eq.schedule }
        if (eventTypes.length === 0) delete schedule[cycle]
        else {
          const kept = procedures ?? eq.schedule[cycle]?.procedures
          schedule[cycle] = { eventTypes, note, weeklyDays, preferredTiming, startTime, endTime, procedures: kept }
        }
        return { ...eq, schedule }
      })
      if (useMockApi || !tenantId) return
      const typeId = typeIdFor(equipmentType)
      if (!typeId) throw new Error(`Unknown equipment type: ${equipmentType}`)
      await equipmentApi.upsertSchedule(tenantId, typeId, cycle, {
        eventTypes,
        note,
        weeklyDays,
        preferredTiming,
        startTime,
        endTime,
        ...(procedures === undefined
          ? {}
          : {
              procedures: Object.entries(procedures).flatMap(([eventTypeCode, p]) =>
                p ? [{ eventTypeCode, procedureTemplateId: p.id }] : [],
              ),
            }),
      })
      await refetch()
    },
    [patchType, refetch, tenantId, typeIdFor],
  )

  const saveCalendar = useCallback<EquipmentMutations['saveCalendar']>(
    async (equipmentType, events) => {
      // Same fallback as `typeIdFor`: without it, removing an event from a type
      // that has no units would compute an empty "before" and delete nothing.
      const before =
        queryClient
          .getQueryData<Equipment[]>(queryKey)
          ?.find((eq) => eq.equipmentType === equipmentType)?.calendar?.events ??
        types.find((t) => t.equipmentType === equipmentType)?.calendar?.events ??
        []
      patchType(equipmentType, (eq) => ({
        ...eq,
        calendar: events.length ? { events } : undefined,
      }))
      if (useMockApi || !tenantId) return
      const typeId = typeIdFor(equipmentType)
      if (!typeId) throw new Error(`Unknown equipment type: ${equipmentType}`)

      const keptIds = new Set(events.map((ev) => ev.id).filter(Boolean))
      const removed = before.filter((ev) => ev.id && !keptIds.has(ev.id))
      const added = events.filter((ev) => !ev.id)
      await Promise.all([
        ...removed.map((ev) => equipmentApi.deleteCalendarEvent(tenantId, ev.id as string)),
        ...added.map((ev) =>
          equipmentApi.createCalendarEvent(tenantId, typeId, {
            date: ev.date,
            event: ev.event,
            note: ev.note,
          }),
        ),
      ])
      await refetch()
    },
    [patchType, queryClient, queryKey, refetch, tenantId, typeIdFor, types],
  )

  const ensureType = useCallback(
    async (
      name: string,
      group: EquipmentGroup,
      preStartChecklist?: { label: string; required?: boolean }[],
      purpose?: EquipmentPurpose,
      maintained?: boolean,
      defaultAssigneeId?: string,
      cleanHoldTimeHours?: number,
    ): Promise<string> => {
      // An existing type keeps the purpose it already has: this is "ensure a
      // type exists", not "reconcile it", and silently reclassifying a type
      // because a unit was added under it would be a surprising side effect.
      const existing = typeIdFor(name)
      if (existing) return existing
      const groupId = resolveGroupId(group)
      if (!groupId) throw new Error(`Unknown equipment group: ${group}`)
      const created = await equipmentApi.createType(tenantId as string, {
        name,
        groupId,
        purpose,
        maintained,
        preStartChecklist,
        defaultAssigneeId,
        ...(cleanHoldTimeHours !== undefined ? { cleanHoldTimeHours } : {}),
      })
      return created.id
    },
    [resolveGroupId, tenantId, typeIdFor],
  )

  const addLocalUnit = useCallback(
    (input: NewUnitInput) => {
      queryClient.setQueryData<Equipment[]>(queryKey, (prev) => [
        ...(prev ?? []),
        {
          id: `local-${input.label}`,
          equipmentId: input.label,
          equipmentType: input.equipmentType,
          group: input.group,
          // Mock mode has no type registry to inherit from, and the Add
          // Equipment form creates a unit rather than a type, so it never
          // carries a purpose. The column default is the honest stand-in.
          purpose: DEFAULT_EQUIPMENT_PURPOSE,
          // A sibling of the same type is the authority when there is one —
          // maintenance is a property of the type, not of this unit.
          maintained:
            prev?.find((eq) => eq.equipmentType === input.equipmentType)?.maintained ?? true,
          location: { site: '—', display: '—' },
          description: '',
          model: input.model ?? '—',
          serial: input.serial ?? '—',
          vendor: input.vendor ?? '—',
          installed: input.installed ?? 0,
          schedule: prev?.find((eq) => eq.equipmentType === input.equipmentType)?.schedule ?? {},
          calendar: prev?.find((eq) => eq.equipmentType === input.equipmentType)?.calendar,
        },
      ])
    },
    [queryClient, queryKey],
  )

  const addEquipment = useCallback<EquipmentMutations['addEquipment']>(
    async (input) => {
      if (useMockApi || !tenantId) {
        addLocalUnit(input)
        return null
      }
      const typeId = await ensureType(input.equipmentType, input.group)
      const created = await equipmentApi.createEquipment(tenantId, {
        label: input.label,
        equipmentTypeId: typeId,
        model: input.model,
        serial: input.serial,
        vendor: input.vendor,
        installedYear: input.installed,
        ...(input.roomId ? { roomId: input.roomId } : {}),
      })
      // The room exists from here on. A floor that fails to apply must not read
      // as a failed add (a retry would only collide with the new room's ID),
      // and the lists must still show the room.
      const floorError = input.floorId
        ? await facilityApi.setRoomFloor(tenantId, created.id, input.floorId).then(() => null, (err: unknown) => err)
        : null
      await refetch()
      if (floorError) {
        throw new Error(
          `${input.label} was added, but it could not be put on that floor (${errorText(floorError)}). ` +
            'Choose the floor again in Edit or Building set-up.',
        )
      }
      return created.id
    },
    [addLocalUnit, ensureType, refetch, tenantId],
  )

  const editEquipment = useCallback<EquipmentMutations['editEquipment']>(
    async (id, input) => {
      // Optimistically patch the unit's display fields.
      queryClient.setQueryData<Equipment[]>(queryKey, (prev) =>
        (prev ?? []).map((eq) =>
          eq.id === id
            ? {
                ...eq,
                equipmentId: input.label,
                // A room's form has none of these: omitted leaves them.
                model: input.model !== undefined ? input.model || '—' : eq.model,
                serial: input.serial !== undefined ? input.serial || '—' : eq.serial,
                vendor: input.vendor !== undefined ? input.vendor || '—' : eq.vendor,
                installed: input.installed ?? eq.installed,
                // Rooms only; omitted leaves them, null clears them.
                ...(input.grade !== undefined ? { grade: input.grade } : {}),
                ...(input.areaSqm !== undefined ? { areaSqm: input.areaSqm } : {}),
              }
            : eq,
        ),
      )
      if (useMockApi || !tenantId) return
      await equipmentApi.updateEquipment(tenantId, id, {
        label: input.label,
        model: input.model || undefined,
        serial: input.serial || undefined,
        vendor: input.vendor || undefined,
        installedYear: input.installed,
        ...(input.grade !== undefined ? { grade: input.grade } : {}),
        ...(input.areaSqm !== undefined ? { areaSqm: input.areaSqm } : {}),
        ...(input.roomId !== undefined ? { roomId: input.roomId } : {}),
      })
      // The rest is saved by now: a floor that fails to apply is reported as
      // just that, after the lists are refreshed to what was actually saved.
      const floorError =
        input.floorId !== undefined
          ? await facilityApi.setRoomFloor(tenantId, id, input.floorId).then(() => null, (err: unknown) => err)
          : null
      await refetch()
      if (floorError) {
        throw new Error(
          `${input.label} was saved, but its floor could not be changed (${errorText(floorError)}). Try the floor again.`,
        )
      }
    },
    [queryClient, queryKey, refetch, tenantId],
  )

  /**
   * Create an equipment TYPE, and only the type.
   *
   * It used to mint N units at the same time, which meant defining "LN2 Dewar"
   * silently produced a machine called `LN2-A1` that nobody had asked for —
   * complete with a label, a maintenance schedule and, for a storage type, a
   * candidate for a storage tree. A type is a definition; units are physical
   * things that arrive later and are added deliberately.
   */
  const addEquipmentType = useCallback<EquipmentMutations['addEquipmentType']>(
    async ({ name, group, purpose, maintained, preStartChecklist, defaultAssigneeId, cleanHoldTimeHours }) => {
      if (useMockApi || !tenantId) {
        setMockTypes(prev => [
          ...prev,
          {
            id: `mock-type-${name}`,
            equipmentType: name,
            group,
            purpose: purpose ?? DEFAULT_EQUIPMENT_PURPOSE,
            maintained: maintained ?? true,
            // Carried through like every other field the caller supplied: the
            // mock row is what the UI reads back, so dropping it let an operator
            // pick a technician, create the type, and find the selection gone.
            defaultAssigneeId,
            units: 0,
            schedule: {},
            preStartChecklist: preStartChecklist?.map((item, i) => ({
              id: `mock-chk-${Date.now()}-${i}`,
              label: item.label,
              required: item.required ?? false,
              order: i,
            })),
          },
        ])
        return
      }
      await ensureType(name, group, preStartChecklist, purpose, maintained, defaultAssigneeId, cleanHoldTimeHours)
      await refetch()
    },
    [ensureType, refetch, tenantId],
  )

  const updateEquipmentType = useCallback<EquipmentMutations['updateEquipmentType']>(
    async ({ id, name, group, purpose, maintained, preStartChecklist, defaultAssigneeId, cleanHoldTimeHours }) => {
      if (useMockApi || !tenantId) return
      const groupId = group ? resolveGroupId(group) : undefined
      if (group && !groupId) throw new Error(`Unknown equipment group: ${group}`)
      await equipmentApi.updateType(tenantId, id, {
        name,
        groupId,
        purpose,
        maintained,
        preStartChecklist,
        defaultAssigneeId,
        ...(cleanHoldTimeHours !== undefined ? { cleanHoldTimeHours } : {}),
      })
      await refetch()
    },
    [refetch, resolveGroupId, tenantId],
  )

  // Renaming/removing a group changes the group name carried by units, so keep
  // the equipment list in sync after group mutations.
  const addGroup = useCallback<GroupMutations['addGroup']>(
    (name) => addGroupBase(name),
    [addGroupBase],
  )
  const renameGroup = useCallback<GroupMutations['renameGroup']>(
    async (id, name) => {
      await renameGroupBase(id, name)
      await refetch()
    },
    [renameGroupBase, refetch],
  )
  const removeGroup = useCallback<GroupMutations['removeGroup']>(
    (id) => removeGroupBase(id),
    [removeGroupBase],
  )

  return {
    data: query.data,
    isLoading: query.isLoading || tenantId === null,
    error: query.error instanceof Error ? query.error.message : null,
    groups,
    types,
    mutations: {
      saveSchedule,
      saveCalendar,
      addEquipment,
      addEquipmentType,
      updateEquipmentType,
      editEquipment,
      addGroup,
      renameGroup,
      removeGroup,
    },
  }
}

/**
 * Put an out-of-service unit back in service, signed and with a reason
 * (z8nrz7fzyk). Everything that shows a unit's status refreshes: the fleet, the
 * Overall View and the facility plans, and Tasks ("Out of service · overdue").
 */
export function useReturnToService() {
  const queryClient = useQueryClient()
  const { selectedTenantId } = useTenant()
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: ReturnToServicePayload }) => {
      if (!selectedTenantId) throw new Error('No tenant selected')
      return equipmentApi.returnToService(selectedTenantId, id, payload)
    },
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ['equipment'] }),
        queryClient.invalidateQueries({ queryKey: [FACILITY_OVERVIEW_KEY] }),
        queryClient.invalidateQueries({ queryKey: [FACILITY_LOCATIONS_KEY] }),
        queryClient.invalidateQueries({ queryKey: ['maintenance-events'] }),
      ]),
  })
}
